import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 200;
const EDGE_MARGIN = 8;
const ANCHOR_GAP = 8;

interface Position {
	top: number;
	left: number;
}

export function ItemIconHover({
	iconUrl,
	hoverIconUrl,
	name,
	themeColorHex,
	imgClassName,
	extraImgClassName,
	onError,
}: {
	iconUrl: string;
	hoverIconUrl?: string | null;
	name: string;
	themeColorHex?: string;
	imgClassName: string;
	extraImgClassName?: string;
	onError?: () => void;
}) {
	const mergedImgClass = `${imgClassName} ${extraImgClassName ?? ""}`;

	if (!hoverIconUrl) {
		return (
			<img
				src={iconUrl}
				alt={name}
				className={mergedImgClass}
				onError={onError}
			/>
		);
	}

	return (
		<ItemIconHoverActive
			iconUrl={iconUrl}
			hoverIconUrl={hoverIconUrl}
			name={name}
			themeColorHex={themeColorHex}
			mergedImgClass={mergedImgClass}
			onError={onError}
		/>
	);
}

function ItemIconHoverActive({
	iconUrl,
	hoverIconUrl,
	name,
	themeColorHex,
	mergedImgClass,
	onError,
}: {
	iconUrl: string;
	hoverIconUrl: string;
	name: string;
	themeColorHex?: string;
	mergedImgClass: string;
	onError?: () => void;
}) {
	const triggerRef = useRef<HTMLImageElement>(null);
	const popupRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [pos, setPos] = useState<Position | null>(null);
	const openTimer = useRef<number | null>(null);
	const closeTimer = useRef<number | null>(null);

	function scheduleOpen() {
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
		if (open || openTimer.current !== null) {
			return;
		}
		openTimer.current = window.setTimeout(() => {
			openTimer.current = null;
			setOpen(true);
		}, OPEN_DELAY_MS);
	}

	function scheduleClose() {
		if (openTimer.current !== null) {
			window.clearTimeout(openTimer.current);
			openTimer.current = null;
		}
		if (!open || closeTimer.current !== null) {
			return;
		}
		closeTimer.current = window.setTimeout(() => {
			closeTimer.current = null;
			setOpen(false);
			setPos(null);
		}, CLOSE_DELAY_MS);
	}

	function forceClose() {
		if (openTimer.current !== null) {
			window.clearTimeout(openTimer.current);
			openTimer.current = null;
		}
		if (closeTimer.current !== null) {
			window.clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
		setOpen(false);
		setPos(null);
	}

	useLayoutEffect(() => {
		if (!open) {
			return;
		}
		const trigger = triggerRef.current;
		const popup = popupRef.current;
		if (!trigger || !popup) {
			return;
		}
		const t = trigger.getBoundingClientRect();
		const p = popup.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		let top = t.top - p.height - ANCHOR_GAP;
		let left = t.left + t.width / 2 - p.width / 2;

		if (top < EDGE_MARGIN) {
			top = t.bottom + ANCHOR_GAP;
			if (top + p.height > vh - EDGE_MARGIN) {
				top = Math.max(EDGE_MARGIN, vh - EDGE_MARGIN - p.height);
			}
		}

		if (left < EDGE_MARGIN) {
			left = EDGE_MARGIN;
		} else if (left + p.width > vw - EDGE_MARGIN) {
			left = vw - EDGE_MARGIN - p.width;
		}

		setPos({ top, left });
	}, [open]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: forceClose is stable via refs/setState
	useEffect(() => {
		if (!open) {
			return;
		}
		function handleScroll() {
			forceClose();
		}
		function handleResize() {
			forceClose();
		}
		window.addEventListener("scroll", handleScroll, true);
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("scroll", handleScroll, true);
			window.removeEventListener("resize", handleResize);
		};
	}, [open]);

	useEffect(() => {
		return () => {
			if (openTimer.current !== null) {
				window.clearTimeout(openTimer.current);
			}
			if (closeTimer.current !== null) {
				window.clearTimeout(closeTimer.current);
			}
		};
	}, []);

	const borderColor = themeColorHex ?? "var(--color-outline)";

	return (
		<>
			<img
				ref={triggerRef}
				src={iconUrl}
				alt={name}
				className={mergedImgClass}
				onError={onError}
				onMouseEnter={scheduleOpen}
				onMouseLeave={scheduleClose}
			/>
			{open
				? createPortal(
						<div
							ref={popupRef}
							role="tooltip"
							onMouseEnter={scheduleOpen}
							onMouseLeave={scheduleClose}
							style={{
								position: "fixed",
								top: pos?.top ?? 0,
								left: pos?.left ?? 0,
								visibility: pos ? "visible" : "hidden",
								zIndex: 50,
								borderLeftColor: borderColor,
							}}
							className="flex w-64 flex-col items-center gap-2 border-l-4 bg-surface-container p-3 shadow-[4px_4px_0_0_rgba(0,0,0,1)] md:w-80"
						>
							<div className="relative aspect-video w-full">
								<img
									src={hoverIconUrl}
									alt=""
									className="absolute inset-0 h-full w-full object-contain"
								/>
							</div>
							<span className="text-center font-mono text-xs text-on-surface uppercase leading-tight tracking-wide">
								{name}
							</span>
						</div>,
						document.body,
					)
				: null}
		</>
	);
}
